<script setup>
import { computed, onActivated, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import CampaignCenterAPI from '../api';
import { formatDateTime, formatNumber } from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const { t, locale } = useI18n();
const router = useRouter();

const TABS = ['blacklist', 'opt_out', 'limits'];
const tab = ref('blacklist');
const settings = ref(null);
const rows = ref([]);
const total = ref(0);
const totalAll = ref(0);
const page = ref(1);
const perPage = ref(50);
const search = ref('');
const source = ref('');
const isLoading = ref(false);
const newNumber = ref('');
const newReason = ref('');
const importText = ref('');
const importReason = ref('');
const importDialogRef = ref(null);
const isImporting = ref(false);
const keywords = ref([]);
const newKeyword = ref('');
const isSavingKeywords = ref(false);

const loadSettings = async () => {
  const { data } = await CampaignCenterAPI.settings();
  settings.value = data;
  keywords.value = [...data.opt_out_keywords];
};
const loadBlacklist = async () => {
  isLoading.value = true;
  try {
    const { data } = await CampaignCenterAPI.blacklist({
      page: page.value,
      q: search.value || undefined,
      source: source.value || undefined,
    });
    rows.value = data.payload;
    total.value = data.meta.total;
    totalAll.value = data.meta.total_all;
    perPage.value = data.meta.per_page;
  } finally {
    isLoading.value = false;
  }
};
const debouncedBlacklist = debounce(loadBlacklist, 400, false);
onActivated(() => {
  loadSettings();
  loadBlacklist();
});
watch(search, () => {
  page.value = 1;
  debouncedBlacklist();
});
watch([source, page], loadBlacklist);

const errorMessage = error =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.ERROR');
const sourceOptions = computed(() => [
  {
    value: '',
    label: t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.SOURCE_ALL'),
  },
  ...['manual', 'opt_out', 'import'].map(value => ({
    value,
    label: t(`WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.SOURCE.${value}`),
  })),
]);

const addNumber = async () => {
  if (!newNumber.value.trim()) return;
  try {
    await CampaignCenterAPI.addToBlacklist(
      newNumber.value.trim(),
      newReason.value.trim()
    );
    newNumber.value = '';
    newReason.value = '';
    useAlert(t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.ADDED'));
    loadBlacklist();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};
const removeRow = async row => {
  try {
    await CampaignCenterAPI.removeFromBlacklist(row.id);
    loadBlacklist();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};
const runImport = async () => {
  if (!importText.value.trim()) return;
  isImporting.value = true;
  try {
    const { data } = await CampaignCenterAPI.importBlacklist(
      importText.value,
      importReason.value.trim()
    );
    useAlert(t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.IMPORT_RESULT', data));
    importText.value = '';
    importDialogRef.value.close();
    loadBlacklist();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isImporting.value = false;
  }
};

const addKeyword = () => {
  const value = newKeyword.value.trim();
  if (value && !keywords.value.includes(value)) keywords.value.push(value);
  newKeyword.value = '';
};
const removeKeyword = index => keywords.value.splice(index, 1);
const resetKeywords = () => {
  keywords.value = [...(settings.value?.default_opt_out_keywords || [])];
};
const saveKeywords = async () => {
  isSavingKeywords.value = true;
  try {
    const { data } = await CampaignCenterAPI.updateSettings({
      opt_out_keywords: keywords.value,
    });
    settings.value = data;
    keywords.value = [...data.opt_out_keywords];
    useAlert(t('WASAPI_CAMPAIGNS.TOAST.SETTINGS_SAVED'));
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSavingKeywords.value = false;
  }
};
const openCampaign = row => {
  if (!row.campaign_id) return;
  router.push({
    name: 'wasapi_campaign_detail',
    params: { channel: 'whatsapp', campaignId: row.campaign_id },
  });
};
const goBack = () => router.push({ name: 'campaigns_whatsapp_index' });
const num = v => formatNumber(v, locale.value);
const limitRows = computed(() => {
  const s = settings.value;
  if (!s) return [];
  const yesNo = v =>
    v
      ? t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.LIMITS.ENABLED')
      : t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.LIMITS.DISABLED');
  return [
    ['RECIPIENTS', num(s.limits.recipients_per_campaign)],
    ['PER_DAY', num(s.limits.campaigns_per_day)],
    ['MAX_RATE', num(s.limits.max_rate_per_minute)],
    ['TIMEZONE', s.timezone],
    ['ENGINE', yesNo(s.engine_enabled)],
    ['WHATSAPP_FLAG', yesNo(s.whatsapp_campaigns_enabled)],
  ];
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="flex flex-col w-full max-w-5xl gap-5 px-6 py-6 mx-auto">
      <header class="flex items-center gap-3">
        <Button
          icon="i-lucide-arrow-left"
          variant="ghost"
          color="slate"
          size="sm"
          @click="goBack"
        />
        <div class="flex flex-col gap-1">
          <h1 class="text-xl font-medium text-n-slate-12">
            {{ t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.DESCRIPTION') }}
          </p>
        </div>
      </header>

      <nav
        class="flex items-center gap-1 overflow-x-auto border-b border-n-weak whitespace-nowrap"
      >
        <button
          v-for="item in TABS"
          :key="item"
          type="button"
          class="px-3 py-2 -mb-px text-sm border-b-2 transition-colors"
          :class="
            tab === item
              ? 'border-n-brand text-n-slate-12 font-medium'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="tab = item"
        >
          {{ t(`WASAPI_CAMPAIGNS.SETTINGS_PAGE.TABS.${item}`) }}
        </button>
      </nav>

      <!-- Blacklist -->
      <div v-if="tab === 'blacklist'" class="flex flex-col gap-4">
        <p class="text-sm text-n-slate-11">
          {{ t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.HINT') }}
        </p>
        <div
          class="flex flex-wrap items-end gap-2 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
        >
          <Input
            v-model="newNumber"
            type="tel"
            :label="t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.NUMBER')"
            placeholder="+55 11 99999-0000"
            class="w-56"
            @keydown.enter.prevent.stop="addNumber"
          />
          <Input
            v-model="newReason"
            :label="t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.REASON')"
            class="w-64"
            @keydown.enter.prevent.stop="addNumber"
          />
          <Button
            :label="t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.ADD')"
            icon="i-lucide-ban"
            color="blue"
            size="sm"
            :disabled="!newNumber.trim()"
            @click="addNumber"
          />
          <span class="flex-1" />
          <Button
            :label="t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.IMPORT')"
            icon="i-lucide-upload"
            variant="faded"
            color="slate"
            size="sm"
            @click="importDialogRef.open()"
          />
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <Input
            v-model="search"
            type="search"
            :placeholder="t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.SEARCH')"
            class="w-72"
          />
          <ComboBox v-model="source" :options="sourceOptions" class="w-48" />
          <span class="flex-1" />
          <span class="text-xs text-n-slate-11">{{
            t(
              'WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.TOTAL',
              { count: totalAll },
              totalAll
            )
          }}</span>
        </div>
        <div v-if="isLoading && !rows.length" class="flex justify-center py-10">
          <Spinner />
        </div>
        <p
          v-else-if="!rows.length"
          class="py-10 text-sm text-center text-n-slate-11"
        >
          {{ t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.EMPTY') }}
        </p>
        <div
          v-else
          class="overflow-x-auto rounded-xl outline outline-1 outline-n-weak"
        >
          <table class="w-full text-sm">
            <thead
              class="text-xs text-left uppercase text-n-slate-11 bg-n-alpha-1"
            >
              <tr>
                <th class="px-3 py-2 font-medium">
                  {{
                    t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.COLUMNS.PHONE')
                  }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{
                    t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.COLUMNS.REASON')
                  }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{
                    t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.COLUMNS.SOURCE')
                  }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{
                    t(
                      'WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.COLUMNS.CAMPAIGN'
                    )
                  }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{
                    t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.COLUMNS.DATE')
                  }}
                </th>
                <th class="px-3 py-2" />
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr v-for="row in rows" :key="row.id" class="hover:bg-n-alpha-1">
                <td class="px-3 py-2 tabular-nums text-n-slate-12">
                  {{ row.phone_number }}
                </td>
                <td class="px-3 py-2 text-n-slate-11 truncate max-w-64">
                  {{ row.reason || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11">
                  {{
                    t(
                      `WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.SOURCE.${row.source}`
                    )
                  }}
                </td>
                <td class="px-3 py-2">
                  <button
                    v-if="row.campaign_id"
                    type="button"
                    class="text-n-blue-11 hover:underline truncate max-w-48"
                    @click="openCampaign(row)"
                  >
                    {{ row.campaign_title || `#${row.campaign_id}` }}
                  </button>
                  <span v-else class="text-n-slate-10">—</span>
                </td>
                <td class="px-3 py-2 whitespace-nowrap text-n-slate-11">
                  {{ formatDateTime(row.created_at, locale) }}
                </td>
                <td class="px-3 py-2 text-right">
                  <Button
                    icon="i-lucide-trash"
                    variant="ghost"
                    color="ruby"
                    size="xs"
                    :title="
                      t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.REMOVE')
                    "
                    @click="removeRow(row)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <PaginationFooter
          v-if="total > perPage"
          :current-page="page"
          :total-items="total"
          :items-per-page="perPage"
          @update:current-page="page = $event"
        />
      </div>

      <!-- Opt-out keywords -->
      <div
        v-else-if="tab === 'opt_out'"
        class="flex flex-col max-w-2xl gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
      >
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.OPT_OUT.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.OPT_OUT.DESCRIPTION') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="(keyword, index) in keywords"
            :key="keyword"
            class="inline-flex items-center gap-1 px-2 py-1 text-sm rounded-md bg-n-alpha-2 text-n-slate-12"
          >
            {{ keyword }}
            <button
              type="button"
              class="i-lucide-x size-3.5 text-n-slate-11 hover:text-n-ruby-11"
              @click="removeKeyword(index)"
            />
          </span>
        </div>
        <Input
          v-model="newKeyword"
          :placeholder="
            t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.OPT_OUT.ADD_PLACEHOLDER')
          "
          class="w-72"
          @keydown.enter.prevent.stop="addKeyword"
        />
        <div class="flex items-center gap-2">
          <Button
            :label="t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.OPT_OUT.SAVE')"
            color="blue"
            size="sm"
            :is-loading="isSavingKeywords"
            :disabled="!keywords.length"
            @click="saveKeywords"
          />
          <Button
            :label="t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.OPT_OUT.RESET')"
            variant="ghost"
            color="slate"
            size="sm"
            @click="resetKeywords"
          />
        </div>
      </div>

      <!-- Limits -->
      <div
        v-else
        class="flex flex-col max-w-2xl gap-3 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
      >
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.LIMITS.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.LIMITS.DESCRIPTION') }}
        </p>
        <dl
          v-if="settings"
          class="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm"
        >
          <template v-for="[key, value] in limitRows" :key="key">
            <dt class="text-n-slate-11">
              {{ t(`WASAPI_CAMPAIGNS.SETTINGS_PAGE.LIMITS.${key}`) }}
            </dt>
            <dd class="text-n-slate-12">{{ value }}</dd>
          </template>
        </dl>
        <Spinner v-else />
      </div>
    </div>

    <Dialog
      ref="importDialogRef"
      :title="t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.IMPORT_TITLE')"
      :description="t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.IMPORT_HELP')"
      :confirm-button-label="
        t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.IMPORT')
      "
      :cancel-button-label="t('WASAPI_CAMPAIGNS.CONFIRM.CANCEL')"
      :is-loading="isImporting"
      :disable-confirm-button="!importText.trim()"
      @confirm="runImport"
    >
      <div class="flex flex-col gap-3">
        <TextArea
          v-model="importText"
          :max-length="100000"
          placeholder="5511999990001&#10;5511999990002"
          auto-height
          max-height="16rem"
        />
        <Input
          v-model="importReason"
          :label="t('WASAPI_CAMPAIGNS.SETTINGS_PAGE.BLACKLIST.REASON')"
        />
      </div>
    </Dialog>
  </section>
</template>
